import i18n from "@/i18n";

const isEn = () => (i18n.resolvedLanguage || i18n.language || "fr").startsWith("en");
// "Vérifier dans le texte": match each summary sentence to the closest passage of the source (free, no AI).
const STOP = new Set(("dans pour avec sans sous entre elle elles ils leur leurs nous vous cette ces cet sont etre avoir fait faire plus moins tres aussi mais donc ainsi comme tout tous toute toutes autre autres meme dont alors apres avant depuis quand chez vers peut peuvent etait ont avait this that with from have were they their there which about into than then them these those also been will would could should what when where while only over such some most more very").split(" "));
const SKIP_FORMATS = new Set(["poeme", "solve", "dissertation", "philo"]);
const SKIP_PREFIX = /^(\*{0,2}\s*)?(c'est comme|it's like|\*?limite|\*?limit\b)/i;
export const MATCH_MIN = 0.4;

const norm = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, " ");
export const stem = (w) => w.slice(0, 6);
export const tokens = (s) => {
  const out = new Set();
  for (const w of norm(s).split(" ")) if (w.length >= 4 && !STOP.has(w)) out.add(stem(w));
  return out;
};

export function buildIndex(source) {
  const text = String(source || "").slice(0, 600000);
  const sentences = [];
  const re = /[^.!?…\n]+[.!?…]*/g;
  let m;
  while ((m = re.exec(text))) {
    const t = m[0].trim();
    if (t.length > 3) sentences.push({ text: t, tok: tokens(t) });
  }
  const inv = new Map();
  sentences.forEach((s, i) => s.tok.forEach((t) => (inv.get(t) || inv.set(t, []).get(t)).push(i)));
  return { sentences, inv, words: text.split(/\s+/).filter(Boolean).length };
}

function bestMatch(claimTok, idx) {
  const hits = new Map();
  claimTok.forEach((t) => (idx.inv.get(t) || []).forEach((i) => hits.set(i, (hits.get(i) || 0) + 1)));
  const cands = [...hits.entries()].sort((a, b) => b[1] - a[1]).slice(0, 200);
  let best = { score: 0, i: -1, span: 1 };
  for (const [i] of cands) {
    for (const span of [1, 2]) {
      const a = idx.sentences[i].tok;
      const b = span === 2 ? idx.sentences[i + 1]?.tok : null;
      let n = 0;
      claimTok.forEach((t) => { if (a.has(t) || (b && b.has(t))) n += 1; });
      const score = n / claimTok.size - (span === 2 ? 0.02 : 0);
      if (score > best.score) best = { score, i, span };
    }
  }
  return best;
}

// Returns markdown with ⟦n⟧ markers + the list of citations and a fidelity status.
export function verifySummary(md, source, format) {
  const base = { md, cites: [], status: "skip" };
  if (!md || SKIP_FORMATS.has(format)) return base;
  const idx = buildIndex(source);
  if (idx.words < 40) return { ...base, status: "notion" };
  const cites = [];
  let inComplement = false;
  const lines = md.split(/\r?\n/).map((line) => {
    const t = line.trim();
    if (/^#{1,4}\s/.test(t)) { inComplement = /compl[ée]ment/i.test(t); return line; }
    if (!t || inComplement || t.startsWith(">") || SKIP_PREFIX.test(t.replace(/^[-•*]\s+/, ""))) return line;
    const pre = (line.match(/^\s*(?:[-•*]|\d+[.)])\s+/) || [""])[0];
    const parts = line.slice(pre.length).split(/(?<=[.!?…])\s+/);
    return pre + parts.map((p) => {
      const tok = tokens(p);
      if (tok.size < 3) return p;
      const b = bestMatch(tok, idx);
      const n = cites.length + 1;
      cites.push({ n, claim: p, tok, ...b, matched: b.score >= MATCH_MIN });
      return `${p}⟦${n}⟧`;
    }).join(" ");
  });
  if (!cites.length) return base;
  const matched = cites.filter((c) => c.matched).length;
  if (cites.length >= 3 && matched / cites.length < 0.3) return { ...base, status: "unreliable" };
  return { md: lines.join("\n"), cites, idx, matched, fidelity: Math.round((matched / cites.length) * 100), status: "ok" };
}

export const citeHtml = (html, cites) =>
  html.replace(/⟦(\d+)⟧/g, (_, n) => {
    const c = cites[Number(n) - 1];
    const ok = c?.matched;
    return `<sup class="sux-cite${ok ? "" : " sux-cite-miss"}"><button type="button" data-cite="${n}" data-testid="cite-${n}" aria-label="${ok ? (isEn() ? "See the passage" : "Voir le passage") : (isEn() ? "Point not found" : "Point non retrouvé")}">[${ok ? n : "?"}]</button></sup>`;
  });
